import { useMemo, useCallback, startTransition } from "react";
import { useNavigate } from "react-router-dom";
import { Navigation, NavigationPageItem } from "types/navigation";
import { useAuth } from "hooks/auth/useAuth";
import { useAdminPermissions } from "hooks/auth/useAdminPermissions";
import { useStakeholderPermissions } from "hooks/auth/useStakeholderPermissions";
import {
  getSuperUserPages,
  getFreelancerPages,
  stakeholderPages,
  studentPages,
} from "router/pages";
import { getStakeholderPages } from "router/pages/stakeholderPages";
import { createEmptyAdminPermissions } from "constants/permissions";
import { createEmptyStakeholderPermissions } from "constants/stakeholderPermissions";
import { useUnifiedPermissions } from "../auth/useUnifiedPermissions";
import {
  AdminPermissionClaims,
  StakeholderPermissionClaims,
} from "@freetech/models";

export const useNavigation = () => {
  const navigate = useNavigate();
  const { role, userInfo } = useAuth();
  const { permissions, isPermissionsLoading } = useUnifiedPermissions();


  const isNavigationLoading = useMemo(() => {
    if (!role) return true;
    if (role === "admin" && isPermissionsLoading) return true;
    if (role === "stakeholder" && isPermissionsLoading) return true;
    return false;
  }, [role, isPermissionsLoading]);


  const navigationItems = useMemo((): Navigation => {
    if (!role || isNavigationLoading || !userInfo) {
      return [];
    }

    switch (role) {
      case "admin":
        return getSuperUserPages(permissions as AdminPermissionClaims);
      case "freelancer":
        return getFreelancerPages(true, userInfo);
      case "stakeholder":
        return getStakeholderPages(permissions as StakeholderPermissionClaims);
      case "student":
        return studentPages;
      default:
        return [];
    }
  }, [role, permissions, isNavigationLoading, userInfo]);


  const findNavItem = useCallback(
    (items: Navigation, path: string): NavigationPageItem | null => {
      for (const item of items) {
        if ("segment" in item && item.segment === path) {
          return item as NavigationPageItem;
        }
        if ("children" in item && item.children) {
          const found = findNavItem(item.children, path);
          if (found) return found;
        }

        if ("slugBabyPattern" in item && "segment" in item && item.segment) {
          const baseSegment = item.segment;
          const pathParts = path.split("/");
          const segmentParts = baseSegment.split("/");

          if (pathParts.length >= segmentParts.length) {
            const isMatch = segmentParts.every(
              (part, index) => part === pathParts[index]
            );
            if (isMatch) {
              return item as NavigationPageItem;
            }
          }
        }
      }
      return null;
    },
    []
  );


  const handleNavigation = useCallback(
    (path?: string) => {
      if (!path) {
        throw new Error("Something weird happened.. path is undefined");
      }
        navigate(`${path}`);
    },
    [navigate]
  );

  return {
    findNavItem,
    handleNavigation,
    navigationItems,
    isNavigationLoading,
  };
};
